import {
  Box,
  Flex,
  Heading,
  Img,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Composition } from '@texas/api/endpoints/compositionApi';
import { metricShortName } from './translations';
import { useTranslation } from 'react-i18next';
import { DefaultDimensionsForm } from './DefaultDimensionForm';
import { MetadataList, MetadataListRow } from '../../MetadataList';
import { clientEndpoints } from '@texas/clientEndpoints';
import { compositionEvents } from '../events';
import { EditComponent } from '../../EditComponent';
import { SimpleNote } from '../../SimpleNote';
import { useEffect } from 'react';
import { MatrixDimensionsForm } from './MatrixDimensionForm';

export function DimensionValuesContainer({
  groupId,
  controllerId,
  composition,
  isReady,
  compositionIndex,
  matrixNodeId,
}: {
  groupId: number;
  controllerId?: number;
  composition: Composition;
  isReady: boolean;
  compositionIndex?: number;
  matrixNodeId?: number;
}) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    return compositionEvents.onDimensionGroupSet.sub((id) => {
      if (controllerId || id !== composition.id) return;
      onOpen();
    });
  }, [onOpen, controllerId, composition.id]);

  if (!composition.dimensionGroup) {
    return null;
  }

  return (
    <>
      <Box>
        <EditComponent onClick={onOpen}>
          <MetadataList>
            {composition.dimensionGroup.dimensions.map((d) => {
              return (
                <MetadataListRow
                  key={d.id}
                  label={d.label}
                  data-value={d.value}
                >
                  {d.value} {metricShortName(d.metric)}
                </MetadataListRow>
              );
            })}
          </MetadataList>
        </EditComponent>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('composition.enterDimensions')}
            {isReady && (
              <SimpleNote
                pt={2}
                color="yellow.400"
                _light={{ color: 'orange.500' }}
              >
                <Text>{t('sizes.selectAndRevertIsReady')}</Text>
              </SimpleNote>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <InnerModal
              composition={composition}
              groupId={groupId}
              controllerId={controllerId}
              compositionIndex={compositionIndex}
              matrixNodeId={matrixNodeId}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

function InnerModal({
  composition,
  groupId,
  controllerId,
  compositionIndex,
  matrixNodeId,
}: {
  composition: Composition;
  groupId: number;
  controllerId?: number;
  compositionIndex?: number;
  matrixNodeId?: number;
}) {
  return (
    <Flex gap={16}>
      {composition.dimensionGroup?.fileIdentifier && (
        <Box>
          <Heading fontSize="md">{composition.dimensionGroup.name}</Heading>
          <Text>{composition.dimensionGroup.description}</Text>
          <Img
            pt={4}
            src={clientEndpoints.previewImage(
              composition.dimensionGroup.fileIdentifier,
              412,
            )}
          />
        </Box>
      )}

      <Flex gap={2} flexDir="column" minW="200px">
        {composition.dimensionGroup?.dimensions.map((d) => {
          if (controllerId)
            return (
              <MatrixDimensionsForm
                key={d.id}
                dimension={d}
                controllerId={controllerId}
                compositionId={composition.id}
                compositionIndex={compositionIndex!}
                matrixNodeId={matrixNodeId!}
                onUpdate={(compositionId, dimensionId, value) =>
                  compositionEvents.onDimensionValueChanged.dispatch({
                    groupId,
                    controllerId,
                    compositionId,
                    dimensionId,
                    value,
                  })
                }
              />
            );
          return (
            <DefaultDimensionsForm
              key={d.id}
              dimension={d}
              compositionId={composition.id}
              onUpdate={(compositionId, dimensionId, value) =>
                compositionEvents.onDimensionValueChanged.dispatch({
                  groupId,
                  controllerId,
                  compositionId,
                  dimensionId,
                  value,
                })
              }
            />
          );
        })}
      </Flex>
    </Flex>
  );
}
